import { Box, ListItem, ListItemText, Skeleton, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import { TFunction, useTranslation } from "react-i18next";

import {
  overlayersAtom,
  addOrUpdateActiveOverlayerAtom,
  updateActiveRadioOverlayerAtom,
  setLayerGroupToNoneAtom,
} from "src/atoms/overlayers";
import { LayerGroup } from "./LayerGroup";
import { Baselayer, Overlayer, LayerManagerContext, LayerGroup as ILayerGroup, LayerGroupWithLayers } from "src/types";
import { baselayersAtom, updateActiveBaselayerAtom } from "src/atoms/baselayers";
import { filterByContext, groupsFromOverlayers } from "src/utils/layer-manager.utils";
import { pipe } from "src/utils/fp.utils";
import { GROUPS } from "src/config/overlayers";
import { LayerManagerSearch } from "src/components/LayerManager/LayerManagerSearch";

interface Props {
  context: LayerManagerContext;
}

export function LayersList(props: Props) {
  const [allOverlayers] = useAtom(overlayersAtom);
  const [, addOrUpdateActiveOverlayer] = useAtom(addOrUpdateActiveOverlayerAtom);
  const [baselayers] = useAtom(baselayersAtom);
  const [, updateActiveBaselayer] = useAtom(updateActiveBaselayerAtom);
  const [, updateActiveRadioOverlayer] = useAtom(updateActiveRadioOverlayerAtom);
  const [, setLayerGroupToNone] = useAtom(setLayerGroupToNoneAtom);
  const [searchValue, setSearchValue] = useState("");
  const { t } = useTranslation();

  const overlayers = useMemo((): Overlayer[] => {
    function filterShowInLayerManager(overlayers: Overlayer[]) {
      return overlayers.filter((overlayer) => overlayer.layerManager?.showInLayerManager);
    }
    return pipe(filterShowInLayerManager, filterByContext(props.context))(allOverlayers);
  }, [allOverlayers, props.context]);
  const groups = useMemo(() => groupsFromOverlayers(overlayers), [overlayers]);

  function handleUpdateOverlayer(layer: Overlayer | null, group: ILayerGroup) {
    if (layer === null) setLayerGroupToNone(group);
    else {
      if (layer) {
        if (layer.group.type === "checkbox") addOrUpdateActiveOverlayer(layer);
        else if (layer.group.type === "radio") updateActiveRadioOverlayer(layer);
      }
    }
  }

  function filterOverlayersByTranslation<T extends Overlayer | Baselayer>(
    layers: T[],
    searchValue: string,
    t: TFunction<"translation", undefined>
  ): T[] {
    function doesTranslationKeyIncludesSearch(value: string, translationKey = "") {
      const translated = translationKey.split("|").reduce((acc, key) => {
        return `${acc} ${t(key)}`;
      }, "");
      const translationNoHTML = translated?.replaceAll(/(<([^>]+)>)/gi, "");
      return translationNoHTML.toLocaleLowerCase().includes(value.toLocaleLowerCase().trim());
    }
    return layers.filter((layer) => doesTranslationKeyIncludesSearch(searchValue, layer.translationKey));
  }

  const filteredOverlayers: Overlayer[] = useMemo(() => {
    return filterOverlayersByTranslation<Overlayer>(overlayers, searchValue, t);
  }, [overlayers, searchValue, t]);

  const filteredBaselayers: Baselayer[] = useMemo(() => {
    return filterOverlayersByTranslation<Baselayer>(baselayers, searchValue, t);
  }, [baselayers, searchValue, t]);

  const mappedGroups = useMemo(() => {
    return groups.reduce((groupsWithData: LayerGroupWithLayers[], group: ILayerGroup) => {
      const layersByGroup = filteredOverlayers.filter((o) => o.group.name === group.name);
      if (layersByGroup.length === 0) return groupsWithData;
      return [
        ...groupsWithData,
        {
          ...group,
          layers: layersByGroup,
        },
      ];
    }, []);
  }, [groups, filteredOverlayers]);
  return render();

  function render() {
    return (
      <Styles>
        <ListItem className="list-header">
          <ListItemText primary={t("label.layerManager")} color="primary" disableTypography />
        </ListItem>
        <LayerManagerSearch searchValue={searchValue} setSearchValue={setSearchValue} />
        {mappedGroups.length === 0 && filteredBaselayers.length === 0 && (
          <Typography className="no-results-label">{t("label.noResultsFound")}</Typography>
        )}
        {mappedGroups.map((group, i) => {
          return (
            <LayerGroup<Overlayer>
              key={`${i}-${searchValue}`}
              group={group}
              layers={group.layers}
              onLayerChange={handleUpdateOverlayer}
              hasNoneOption={searchValue === ""}
            />
          );
        })}
        {filteredBaselayers.length > 0 && (
          <LayerGroup<Baselayer>
            group={GROUPS.baseLayers}
            layers={filteredBaselayers}
            onLayerChange={(layer) => {
              if (layer) updateActiveBaselayer(layer);
            }}
          />
        )}
      </Styles>
    );
  }
}

export function LayersListSkeleton() {
  const { t } = useTranslation();

  return (
    <Styles>
      <ListItem className="list-header">
        <ListItemText primary={t("label.layerManager")} color="primary" disableTypography />
      </ListItem>
      <Box p={2} display="flex" flexDirection="column" gap={2}>
        <Skeleton variant="rectangular" />
        <Skeleton variant="rectangular" />
        <Skeleton variant="rectangular" />
      </Box>
    </Styles>
  );
}

const Styles = styled("div")`
  flex-shrink: 1;
  flex-grow: 3;
  overflow: auto;
  width: 100%;
  background-color: white;

  /* for Firefox */
  min-height: 0;

  .no-results-label {
    padding: ${({ theme }) => theme.spacing(2)};
    color: ${({ theme }) => theme.palette.text.primary};
  }

  .list-header {
    position: sticky;
    top: 0;
    background-color: #f7f7f7;
    z-index: 1;
    padding: ${({ theme }) => theme.spacing(2, 3, 2, 2)};
    border-bottom: 1px solid #e7e7e7;

    .MuiListItemText-root {
      color: ${({ theme }) => theme.palette.primary.main};
    }
  }

  .list-item-content {
    background-color: white;
    height: 100%;
    display: flex;
    flex-direction: column;
    flex: 1 1 100%;
  }

  .MuiCollapse-wrapper {
    overflow: auto;
  }
`;
